/**
 * The apps the browser-RUM extension reports on, and how NOVA names them.
 *
 * Shared by two features, so it lives here rather than in either: the Dynamics
 * board's report queue (`features/dynamics/reports-section.tsx`) labels the app
 * a person complained about, and the experience board
 * (`features/experience/`) is organised by app end to end.
 *
 * Two rules this module exists to hold:
 *
 * 1. **No enum mirrors live data** (dashboard design rules §8). The five ids are
 *    a contract — they become an `App` column value the workspace groups by —
 *    but the payloads carry `app` as a plain string. An id nobody has seen
 *    before therefore renders itself, in the "Other" slot at the end of the
 *    order, instead of throwing or vanishing.
 * 2. **What each app can be measured on differs**, and the board has to say so.
 *    Only F&O has a denominator for coverage (`AppPageViews` active users), and
 *    Teams contributes a host with no page key at all (PRIVACY: "Teams:
 *    nothing"), so a Teams row can never name a context. `RUM_APP_META` carries
 *    both facts beside the label so no panel has to re-derive them.
 *
 * Pure: type-only imports, no runtime dependency, so `node --test` can exercise
 * it directly (`tests/rum-apps.test.mjs`).
 */

import type { FoConsoleRumApp } from "@/lib/api";

/** The five ids in board order — most people, most measurable, first. */
export const RUM_APPS = ["fo", "ce", "powerbi", "sharepoint", "teams"] as const;

/** The bucket an id nobody has seen before falls into. */
export const RUM_APP_OTHER = "other";

/** What each app is called on the board. */
export const RUM_APP_LABEL: Record<FoConsoleRumApp, string> = {
  fo: "Dynamics 365 F&O",
  ce: "Dynamics 365 CE",
  powerbi: "Power BI",
  sharepoint: "SharePoint & OneDrive",
  teams: "Teams",
};

/** The short form for a chart legend or a chip, where the long name would wrap. */
export const RUM_APP_SHORT_LABEL: Record<FoConsoleRumApp, string> = {
  fo: "F&O",
  ce: "CE",
  powerbi: "Power BI",
  sharepoint: "SharePoint",
  teams: "Teams",
};

export interface RumAppMeta {
  /** Whether this app has a denominator to compute browser coverage against. */
  hasCoverage: boolean;
  /**
   * What the app's page key is, in words — or `null` where the privacy rules
   * collect none, in which case a context column must say "not collected"
   * rather than showing a blank.
   */
  contextName: string | null;
}

/**
 * Per-app facts the panels need beside the label. The `contextName` values are
 * the page keys the concept's §11 privacy rules permit and nothing more: for
 * SharePoint the site-collection segment only, for Teams nothing at all.
 */
export const RUM_APP_META: Record<FoConsoleRumApp, RumAppMeta> = {
  fo: { hasCoverage: true, contextName: "form" },
  ce: { hasCoverage: false, contextName: "entity and page type" },
  powerbi: { hasCoverage: false, contextName: "report or dashboard id" },
  sharepoint: { hasCoverage: false, contextName: "site collection" },
  teams: { hasCoverage: false, contextName: null },
};

/** True when `app` is one of the five contract ids. */
export function isKnownRumApp(app: string | null | undefined): app is FoConsoleRumApp {
  return RUM_APPS.some((known) => known === app);
}

/**
 * The app's name on the board. An unknown id renders verbatim — it is real data
 * the extension sent, and hiding it would understate the estate (§8).
 */
export function rumAppLabel(app: string | null | undefined): string {
  const id = (app ?? "").trim();
  if (id === "") {
    return "unknown app";
  }
  return isKnownRumApp(id) ? RUM_APP_LABEL[id] : id;
}

/** The short name, same fallback. */
export function rumAppShortLabel(app: string | null | undefined): string {
  const id = (app ?? "").trim();
  if (id === "") {
    return "unknown";
  }
  return isKnownRumApp(id) ? RUM_APP_SHORT_LABEL[id] : id;
}

/** Whether this app can carry a coverage ratio at all. Unknown apps cannot. */
export function rumAppHasCoverage(app: string | null | undefined): boolean {
  const id = (app ?? "").trim();
  return isKnownRumApp(id) ? RUM_APP_META[id].hasCoverage : false;
}

/**
 * What this app's page key is called, or `null` when none is collected. An
 * unknown app returns the generic word rather than `null`: the extension did
 * send a context for it, so claiming none is collected would be wrong.
 */
export function rumAppContextName(app: string | null | undefined): string | null {
  const id = (app ?? "").trim();
  return isKnownRumApp(id) ? RUM_APP_META[id].contextName : "page key";
}

/**
 * Sort ids into board order: the five contract apps first in their fixed order,
 * then anything else alphabetically. Fixed order matters because app is a
 * categorical identity — a filter must not repaint the survivors (§3.3).
 */
export function sortRumApps<T>(items: readonly T[], appOf: (item: T) => string): T[] {
  const rank = (app: string): number => {
    const index = RUM_APPS.findIndex((known) => known === app);
    return index < 0 ? RUM_APPS.length : index;
  };
  return [...items].sort((a, b) => {
    const byRank = rank(appOf(a)) - rank(appOf(b));
    return byRank !== 0 ? byRank : appOf(a).localeCompare(appOf(b));
  });
}
